/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import AbstractModule from "../abstract_module";
import IPickingModule from "./scene_picking_interface";
import {Debug, RenderDocument} from "js_renderer";
import PoiPicker from "./poi_picker";
import GeoElementsPicker from "./geo_elements_picker";
import PrimitivePointPicker from "./primitive_point_picker";
import ElementPicker from "./element_picker";
import {vec2, vec3} from "gl-matrix";
import Picker from "./picker";
import IElement from "../../elements/element_interface";
import IAbstractElement from "../../elements/abstract_element_interface";
import Scene from "../../scene";
import ViewModule from "../view/scene_view";
import Settings from "../settings";

export default
class PickingModule extends AbstractModule implements IPickingModule {

    protected static _typeName: string = "picking";
    private _poiPicker: PoiPicker;
    private _geoElementsPicker: GeoElementsPicker;
    private _primitivePointPicker: PrimitivePointPicker;
    private _elementPicker: ElementPicker;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._type = PickingModule._typeName;

        this._poiPicker = null;
        this._geoElementsPicker = null;
        this._primitivePointPicker = null;
        this._elementPicker = null;

        this._defaultSettings = {
            disableCameraRotation: false
        };

        this._settings = Settings.clone(this._defaultSettings);
    }

    public static getClassType(): string {
        return PickingModule._typeName;
    }

    public startPrimitivePointPicking(aCallbacks: any, aHighlightOnMove: boolean): boolean {
        if (this._primitivePointPicker) {
            return false;
        }

        this._primitivePointPicker = new PrimitivePointPicker(this.renderDocument);
        this._primitivePointPicker.startPicking();

        this._primitivePointPicker.setOnPickCallbacks(aCallbacks);

        this._primitivePointPicker.setHighlightOnMove(aHighlightOnMove);

        return true;
    }

    public isCameraRotationDisabled(): boolean {
        return this._settings.disableCameraRotation;
    };

    public disableCameraRotation(aValue: boolean) {
        this._settings.disableCameraRotation = aValue;
    }

    public stopPrimitivePointPicking(): boolean {
        if (!this._primitivePointPicker) {
            return false;
        }

        this._primitivePointPicker.stopPicking();
        this._primitivePointPicker = null;
        this._settings.disableCameraRotation = false;

        return true;
    }

    public pickSAPIElement(): IAbstractElement {
        if (!this._elementPicker) {
            return null;
        }

        return this._elementPicker.pickSceneElement([]);
    }

    public getPickedSAPIElement(): IElement {
        if (!this._elementPicker) {
            return null;
        }

        return this._elementPicker.getPickedSAPIElement();
    }

    public startElementPicking(aCallbacks: any): boolean {
        if (this._elementPicker) {
            return false;
        }

        this._elementPicker = new ElementPicker(this.renderDocument);
        this._elementPicker.startPicking();

        this._elementPicker.setOnPickCallbacks(aCallbacks);

        return true;
    }

    public pauseElementPicking(aValue: boolean): boolean {
        if (!this._elementPicker) {
            return false;
        }

        this._elementPicker.pausePicking(aValue);

        return true;
    }

    public isElementPickingPaused(): boolean {
        if (!this._elementPicker) {
            return false;
        }

        return this._elementPicker.isPaused();
    }

    public stopElementPicking(): boolean {
        if (!this._elementPicker) {
            return false;
        }

        this._elementPicker.stopPicking();
        this._elementPicker = null;
        this._settings.disableCameraRotation = false;

        return true;
    }

    public startGroupTreeGroupPicking(aCallbacks: any) {
        /*if (this._gtGroupPicker) {
            return false;
        }

        this._gtGroupPicker = new GroupTreeGroupPicker(this.sceneAPI, (this.sceneAPI.getModule(ViewModule) as ViewModule)._getActiveView());
        this._gtGroupPicker.startPicking();

        this._gtGroupPicker.setOnPickCallbacks(aCallbacks);

        return true;*/
    }

    public stopGroupTreeGroupPicking() {
        /*if (!this._gtGroupPicker) {
            return false;
        }

        this._gtGroupPicker.stopPicking();
        this._gtGroupPicker = null;
        this._settings.disableCameraRotation = false;

        return true;*/
    }

    public startPoiPicking(aCallbacks): boolean {
        if (this._poiPicker) {
            return false;
        }

        this._poiPicker = new PoiPicker(this.renderDocument);
        this._poiPicker.startPicking();

        this._poiPicker.setOnPickCallbacks(aCallbacks);

        return true;
    }

    public pausePoiPicking(aValue: boolean): boolean {
        if (!this._poiPicker) {
            return false;
        }

        this._poiPicker.pausePicking(aValue);

        return true;
    }

    public isPoiPickingPaused(): boolean {
        if (!this._poiPicker) {
            return false;
        }

        return this._poiPicker.isPaused();
    }

    public stopPoiPicking(): boolean {
        if (!this._poiPicker) {
            return false;
        }

        this._poiPicker.stopPicking();
        this._poiPicker = null;
        this._settings.disableCameraRotation = false;

        return true;
    }

    public startGeoElementsPicking(aCallbacks: any): boolean {
        if (this._geoElementsPicker) {
            return false;
        }

        this._geoElementsPicker = new GeoElementsPicker(this.renderDocument);
        this._geoElementsPicker.startPicking();

        this._geoElementsPicker.setOnPickCallbacks(aCallbacks);

        return true;
    }

    public pauseGeoElementsPicking(aValue: boolean): boolean {
        if (!this._geoElementsPicker) {
            return false;
        }

        this._geoElementsPicker.pausePicking(aValue);

        return true;
    }

    public isGeoElementsPickingPaused(): boolean {
        if (!this._geoElementsPicker) {
            return false;
        }

        return this._geoElementsPicker.isPaused();
    }

    public stopGeoElementsPicking(): boolean {
        if (!this._geoElementsPicker) {
            return false;
        }

        this._geoElementsPicker.stopPicking();
        this._geoElementsPicker = null;
        this._settings.disableCameraRotation = false;

        return true;
    }

    /**
     * Get 3D position in model space based on 2D canvas position (e.g. mouse position). If the 2D position is not passed to the arguments,
     * then current mouse position is taken for computing the 3D position. The returned 3D point is based on depth value so its accuracy depends on
     * the depth buffer precision, model size, near and far settings etc.  The advantage is that it is the fastest solution for picking in webgl.
     * If you need precise picking, please, use the getPrecise3DPosition method.
     *
     * @param {number} aScreenPositionX  Mouse x position.
     * @param {number} aScreenPositionY  Mouse y position.
     * @returns {[x, y, z]} 3D point in model space
     */
    public get3DPosition(aPagePositionX: number, aPagePositionY: number): vec3 {
        let view = this.sceneAPI.viewModeModule.getActiveView();

        if (!view) {
            return null;
        }


        let physMousePosition = null;
        if (aPagePositionX === undefined || aPagePositionY === undefined ||
            aPagePositionX === null || typeof aPagePositionY === null) {
            physMousePosition = view.getLastMousePosition();
        } else {
            physMousePosition = this.sceneAPI.getCanvas().getPhysMousePositionFromPagePosition([
                aPagePositionX,
                aPagePositionY
            ]);
        }

        if (!physMousePosition) {
            return null;
        }

        let points = this.renderDocument.renderForValues(view, {
            type: "cdwk_depth",
            readPosition: [physMousePosition[0], physMousePosition[1]]
        });

        // take point at center (like in zoom)
        if (!points[0]) {
            let depth = view.getDepthValueOfPoint(view.document.getBoundingBox().getCenter());
            points = [view.projectWindowPointTo3DPoint(physMousePosition[0], physMousePosition[1], depth)];
        }

        return points[0];
    }

    /***
     * Compute y value by picking the geometry from [x,z] position on top of bounding box of the scene and pointing down.
     * @param a2DPoint [x, z] position
     * @returns {*}
     */
    public _getYValue(a2DPoint: number[]): number {
        if (!this.sceneAPI.viewModeModule.getActiveView()) {
            return null;
        }

        // move camera to position defined by the a2DPoint and pointing down
        let view = this.sceneAPI.viewModeModule.getActiveView();

        // prepare the matrix for ground picking from top of the scene
        let camera = view._controls._camera;
        let min = this.renderDocument.getBoundingBox().min;
        let max = this.renderDocument.getBoundingBox().max;
        camera.backup();

        // We cannot use the max[1] value to move the camera to the top looking down on the ground
        // as the precision is too small in the depth buffer so that we cannot go enough close to the ground on iPhone6.
        // So we set the near plane to 100.0 value and offset the camera position on the top of the scene bounding box
        // by this value too. This will increase the precision on the far plane.
        let nearValueOffset = 100.0;
        let gcCamFov = 0.0001;
        camera.setCameraPositionAndTarget(
            vec3.fromValues(a2DPoint[0], max[1] + nearValueOffset, a2DPoint[1]),
            vec3.fromValues(a2DPoint[0], min[1], a2DPoint[1])
        );
        camera.setCameraUpVector(
            vec3.fromValues(0, 0, 1) //do not use up vector [0,1,0] when camera is looking down, because the view vector direction 0,-1,0 causes that the model view matrix is computed strangely
        );
        camera.setFieldOfView(gcCamFov);
        camera.forceNearValue(nearValueOffset);

        // use the ground collision camera to perform the pick via this controls
        let points = this.renderDocument.renderForValues(view, {
            type: "cdwk_depth_custom_camera",
            readPosition: [
                view.position[0] + (view.size[0] / 2.0),
                view.canvas.getContentSize()[1] - (view.position[1] + (view.size[1] / 2.0))
            ]
        });

        camera.restore();
        if (!points[0]) {
            return null;
        }

        return points[0][1];
    }

    public getPrecise3DPosition(aPagePositionX: number, aPagePositionY: number): vec3 {
        if (!this.sceneAPI.viewModeModule.getActiveView()) {
            return null;
        }

        let physMousePosition = null;
        if (aPagePositionX === undefined || aPagePositionY === undefined ||
            aPagePositionX === null || typeof aPagePositionY === null) {
            physMousePosition = this.sceneAPI.viewModeModule.getActiveView().getLastMousePosition();
        } else {
            physMousePosition = this.sceneAPI.getCanvas().getPhysMousePositionFromPagePosition([
                aPagePositionX,
                aPagePositionY
            ]);
        }

        if (!physMousePosition) {
            return null;
        }

        let picker = new Picker(this.renderDocument);

        let intersectionPoint = picker.getIntersectionPoint(physMousePosition);
        if (!intersectionPoint) {
            intersectionPoint = picker.getIntersectionPoint(physMousePosition);
            Debug.logInfo("No intersection found using the getPrecise3DPosition so the approximate get3DPosition method was used to get the 3D intersection point.");
            return this.get3DPosition(aPagePositionX, aPagePositionY);
        }

        return intersectionPoint;
    }

    public viewChanged() {
    }

    public viewResized(aContentSize: vec2) {
    }
}

Scene.registerSceneAPIModule(PickingModule);
